import { scaleLinear, scaleUtc } from '@visx/scale';
import { useRef } from 'react';

import { Box, useTheme } from '@simplywallst/ui-core';

import type { PortfolioPerformanceTimeseries } from '@/hooks/portfolio/usePortfolioPerformanceTimeseries';
import { useChartData } from '../../hooks';

import { StyledSVG } from './styled';
import { AreaClosed, LinePath } from '@visx/shape';
import { curveMonotoneX } from 'd3-shape';
import { getTrend } from '../../utils';

const getFill = (trend: ReturnType<typeof getTrend>) => {
  if (trend === 'positive') return 'url(#positive-chart)';
  if (trend === 'negative') return 'url(#negative-chart)';
};

const getStrokeColor = (trend: ReturnType<typeof getTrend>) => {
  if (trend === 'positive') return 'good';
  if (trend === 'negative') return 'bad';
  return 'white';
};

interface Props {
  width: number;
  height: number;
  timeseries: PortfolioPerformanceTimeseries;
}

export const Chart = ({ width, height, timeseries }: Props) => {
  const theme = useTheme();

  const { current: xScale } = useRef(scaleUtc<number>());
  const { current: yScale } = useRef(scaleLinear<number>());

  const { xDomain, yDomain, normalisedPerformanceTimeseriesData } =
    useChartData({
      timeseries,
    });

  xScale.range([0, width]);
  xScale.domain(xDomain);
  yScale.range([height, 0]);
  yScale.domain(yDomain);

  const trend = getTrend(normalisedPerformanceTimeseriesData);
  const fill = getFill(trend);
  const strokeColor = getStrokeColor(trend);

  return (
    <>
      <Box position="relative">
        <StyledSVG
          y="0"
          x="0"
          width={width}
          height={height}
          role="application"
          aria-label="Portfolio Performance Chart"
        >
          <svg aria-label="Portfolio Performance Timeseries Plot">
            <LinePath
              data={normalisedPerformanceTimeseriesData}
              x={([date]) => xScale(date)}
              y={([_, value]) => yScale(value || 0)}
              stroke={theme.color[strokeColor]}
              strokeWidth={2}
              strokeLinecap="round"
              opacity={1}
              curve={curveMonotoneX}
            />
            {trend !== 'neutral' && (
              <AreaClosed
                data={normalisedPerformanceTimeseriesData}
                x={([date]) => xScale(date)}
                y={([_, value]) => yScale(value || 0)}
                y0={() => yScale.range()[0]}
                yScale={yScale}
                fill={fill}
                fillOpacity={0.9}
                curve={curveMonotoneX}
              />
            )}
          </svg>
        </StyledSVG>
      </Box>
    </>
  );
};
